import type {
  EmployeePickerItem,
  FuhrparkListResponse,
  VehicleDetail,
  VehicleDocument,
  VehicleFuelCard,
  VehicleFuelTransaction,
  VehicleListItem,
  VehicleReminder,
  VehicleRepair,
  VehicleServiceEvent,
} from './types';

async function readJson<T>(res: Response): Promise<T> {
  const body = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new Error(body.error || 'Anfrage fehlgeschlagen.');
  return body;
}

export async function fetchFuhrparkList(): Promise<FuhrparkListResponse> {
  const res = await fetch('/api/fuhrpark/vehicles', { credentials: 'include' });
  return readJson<FuhrparkListResponse>(res);
}

export async function fetchVehicleDetail(id: string): Promise<VehicleDetail> {
  const res = await fetch(`/api/fuhrpark/vehicles/${encodeURIComponent(id)}`, { credentials: 'include' });
  return readJson<VehicleDetail>(res);
}

export async function createVehicle(payload: Record<string, unknown>): Promise<VehicleListItem> {
  const res = await fetch('/api/fuhrpark/vehicles', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await readJson<{ item: VehicleListItem }>(res);
  return data.item;
}

export async function patchVehicle(id: string, payload: Record<string, unknown>): Promise<VehicleListItem> {
  const res = await fetch(`/api/fuhrpark/vehicles/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await readJson<{ item: VehicleListItem }>(res);
  return data.item;
}

export async function deleteVehicle(id: string): Promise<void> {
  const res = await fetch(`/api/fuhrpark/vehicles/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  await readJson<{ ok: boolean }>(res);
}

export async function uploadVehiclePhoto(id: string, file: File): Promise<void> {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`/api/fuhrpark/vehicles/${encodeURIComponent(id)}/photo`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  await readJson<{ ok: boolean }>(res);
}

export function vehiclePhotoUrl(id: string): string {
  return `/api/fuhrpark/vehicles/${encodeURIComponent(id)}/photo`;
}

export async function createReminder(vehicleId: string, payload: Record<string, unknown>): Promise<VehicleReminder> {
  const res = await fetch(`/api/fuhrpark/vehicles/${encodeURIComponent(vehicleId)}/reminders`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await readJson<{ item: VehicleReminder }>(res);
  return data.item;
}

export async function patchReminder(
  vehicleId: string,
  reminderId: string,
  payload: Record<string, unknown>,
): Promise<VehicleReminder> {
  const res = await fetch(
    `/api/fuhrpark/vehicles/${encodeURIComponent(vehicleId)}/reminders/${encodeURIComponent(reminderId)}`,
    {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
  );
  const data = await readJson<{ item: VehicleReminder }>(res);
  return data.item;
}

export async function completeReminder(
  vehicleId: string,
  reminderId: string,
  payload: {
    performed_on: string;
    mileage?: string;
    workshop?: string;
    cost?: string;
    note?: string;
    file?: File | null;
  },
): Promise<{ reminder: VehicleReminder | null; service_event: VehicleServiceEvent | null }> {
  const form = new FormData();
  form.append('performed_on', payload.performed_on);
  if (payload.mileage) form.append('mileage', payload.mileage);
  if (payload.workshop) form.append('workshop', payload.workshop);
  if (payload.cost) form.append('cost', payload.cost);
  if (payload.note) form.append('note', payload.note);
  if (payload.file) form.append('file', payload.file);
  const res = await fetch(
    `/api/fuhrpark/vehicles/${encodeURIComponent(vehicleId)}/reminders/${encodeURIComponent(reminderId)}/complete`,
    { method: 'POST', credentials: 'include', body: form },
  );
  return readJson(res);
}

export async function deleteReminder(vehicleId: string, reminderId: string): Promise<void> {
  const res = await fetch(
    `/api/fuhrpark/vehicles/${encodeURIComponent(vehicleId)}/reminders/${encodeURIComponent(reminderId)}`,
    { method: 'DELETE', credentials: 'include' },
  );
  await readJson<{ ok: boolean }>(res);
}

export async function deleteRepair(vehicleId: string, repairId: string): Promise<void> {
  const res = await fetch(
    `/api/fuhrpark/vehicles/${encodeURIComponent(vehicleId)}/repairs/${encodeURIComponent(repairId)}`,
    { method: 'DELETE', credentials: 'include' },
  );
  await readJson<{ ok: boolean }>(res);
}

export async function deleteVehiclePhoto(vehicleId: string): Promise<void> {
  const res = await fetch(`/api/fuhrpark/vehicles/${encodeURIComponent(vehicleId)}/photo`, {
    method: 'DELETE',
    credentials: 'include',
  });
  await readJson<{ ok: boolean }>(res);
}

export async function createRepair(vehicleId: string, payload: Record<string, unknown>): Promise<VehicleRepair> {
  const res = await fetch(`/api/fuhrpark/vehicles/${encodeURIComponent(vehicleId)}/repairs`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await readJson<{ item: VehicleRepair }>(res);
  return data.item;
}

export async function patchRepair(
  vehicleId: string,
  repairId: string,
  payload: Record<string, unknown>,
): Promise<VehicleRepair> {
  const res = await fetch(
    `/api/fuhrpark/vehicles/${encodeURIComponent(vehicleId)}/repairs/${encodeURIComponent(repairId)}`,
    {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
  );
  const data = await readJson<{ item: VehicleRepair }>(res);
  return data.item;
}

export async function upsertFuelCard(vehicleId: string, payload: Record<string, unknown>): Promise<VehicleFuelCard> {
  const res = await fetch(`/api/fuhrpark/vehicles/${encodeURIComponent(vehicleId)}/fuel-card`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await readJson<{ item: VehicleFuelCard }>(res);
  return data.item;
}

export async function deleteFuelCard(vehicleId: string): Promise<void> {
  const res = await fetch(`/api/fuhrpark/vehicles/${encodeURIComponent(vehicleId)}/fuel-card`, {
    method: 'DELETE',
    credentials: 'include',
  });
  await readJson<{ ok: boolean }>(res);
}

export async function createFuelTx(
  vehicleId: string,
  payload: Record<string, unknown>,
): Promise<VehicleFuelTransaction> {
  const res = await fetch(`/api/fuhrpark/vehicles/${encodeURIComponent(vehicleId)}/fuel-transactions`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await readJson<{ item: VehicleFuelTransaction }>(res);
  return data.item;
}

export async function patchFuelTx(
  vehicleId: string,
  txId: string,
  payload: Record<string, unknown>,
): Promise<VehicleFuelTransaction> {
  const res = await fetch(
    `/api/fuhrpark/vehicles/${encodeURIComponent(vehicleId)}/fuel-transactions/${encodeURIComponent(txId)}`,
    {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
  );
  const data = await readJson<{ item: VehicleFuelTransaction }>(res);
  return data.item;
}

export async function deleteFuelTx(vehicleId: string, txId: string): Promise<void> {
  const res = await fetch(
    `/api/fuhrpark/vehicles/${encodeURIComponent(vehicleId)}/fuel-transactions/${encodeURIComponent(txId)}`,
    { method: 'DELETE', credentials: 'include' },
  );
  await readJson<{ ok: boolean }>(res);
}

export async function uploadVehicleDocument(
  vehicleId: string,
  file: File,
  category: string,
  displayName?: string,
): Promise<VehicleDocument> {
  const form = new FormData();
  form.append('file', file);
  form.append('category', category);
  if (displayName) form.append('display_name', displayName);
  const res = await fetch(`/api/fuhrpark/vehicles/${encodeURIComponent(vehicleId)}/documents`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  const data = await readJson<{ item: VehicleDocument }>(res);
  return data.item;
}

export function vehicleDocumentUrl(vehicleId: string, docId: string, download = false): string {
  const q = download ? '?download=1' : '';
  return `/api/fuhrpark/vehicles/${encodeURIComponent(vehicleId)}/documents/${encodeURIComponent(docId)}${q}`;
}

export async function deleteVehicleDocument(vehicleId: string, docId: string): Promise<void> {
  const res = await fetch(
    `/api/fuhrpark/vehicles/${encodeURIComponent(vehicleId)}/documents/${encodeURIComponent(docId)}`,
    { method: 'DELETE', credentials: 'include' },
  );
  await readJson<{ ok: boolean }>(res);
}

export async function fetchEmployeePicker(): Promise<EmployeePickerItem[]> {
  const res = await fetch('/api/employees/picker', { credentials: 'include' });
  const data = await readJson<{ items: EmployeePickerItem[] }>(res);
  return data.items ?? [];
}
